import React, { useState, useEffect } from "react";

/**
 * CookieConsentBanner Component
 *
 * This component displays a cookie consent banner at the bottom of the screen.
 * It manages user consent for cookies and initializes Google Analytics if consent is accepted.
 *
 * Features:
 * - Displays the banner if no consent decision is stored in localStorage.
 * - Initializes Google Analytics when the user accepts cookies.
 * - Updates localStorage with the user's choice.
 * - Provides buttons to accept or decline cookies.
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Check for existing cookie consent status on component mount
  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setIsVisible(true);
    } else if (consent === "accepted") {
      // Initialize Google Analytics if consent was already given
      loadGoogleAnalytics();
    }
  }, []);

  // Handle accepting cookies
  const acceptCookies = () => {
    localStorage.setItem("cookie_consent", "accepted");
    setIsVisible(false);
    loadGoogleAnalytics(); // Initialize Google Analytics after accepting cookies
  };

  // Handle declining cookies
  const declineCookies = () => {
    localStorage.setItem("cookie_consent", "declined");
    setIsVisible(false);
    // Do not initialize analytics if cookies are declined
  };

  // Function to load Google Analytics script
  const loadGoogleAnalytics = () => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
    
      const gtag = (...args: any[]) => {
        window.dataLayer.push(args);
      };
    
      // Insert the Google Analytics script
      const script = document.createElement("script");
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-81P8CYFSRW";
      script.async = true;
      document.head.appendChild(script);
    
      // Initialize GA after script loads
      script.onload = () => {
        gtag("js", new Date());
        gtag("config", "G-81P8CYFSRW");
      };
    }
  };

  // If the banner should not be visible, return null
  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        backgroundColor: "#333",
        color: "#fff",
        padding: "15px",
        textAlign: "center",
        zIndex: 1000,
      }}
    >
      <p style={{ margin: 0 }}>
        We use cookies to improve your experience. By using our site, you agree to our{" "}
        <a
          href="https://map4nomads.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#4CAF50", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>
        .
      </p>

      <div style={{ marginTop: "10px" }}>
        {/* Accept Button */}
        <button
          onClick={acceptCookies}
          style={{
            marginRight: "10px",
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Accept
        </button>

        {/* Decline Button */}
        <button
          onClick={declineCookies}
          style={{
            padding: "10px 20px",
            backgroundColor: "#f44336",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Decline
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
